<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue'
import { StripeCheckoutPopup, useStripeCheckout } from '@/composables/payment'
import { useUserStore } from '@/stores'
import { useAxonPageView } from '@/composables/axon/useAxonPageView'
import { useAxonViewItem } from '@/composables/axon/useAxonViewItem'
import { useGameFrame } from '@/composables/useGameFrame'

const userStore = useUserStore()
const { reportPageView } = useAxonPageView()

const {
  route,
  router,
  iframeRef,
  gameUrl,
  showLoading,
  percent,
  currentTip,
  stopTips,
} = useGameFrame({
  onLoaded: reportGameViewItem,
  onMessage: onGameMessage,
})

const coverUrl = computed(() => (route.query.coverUrl as string) || sessionStorage.getItem('gameCoverUrl') || '')
// Loading 动画 MP4：详情页 onRead 时写入 sessionStorage；为空/失败时静态封面兜底
const loadingAnimationUrl = sessionStorage.getItem('gameLoadingAnimationUrl') || ''

const { reportViewItem } = useAxonViewItem()
function reportGameViewItem() {
  const contentId = (route.query.contentId as string) || ''
  if (!contentId)
    return
  reportViewItem({
    contentId,
    contentName: (route.query.contentName as string) || '',
    coverUrl: coverUrl.value,
  })
}

// ===== Stripe 内嵌支付（游戏内）=====
const {
  stripeClientSecret,
  stripeVisible,
  openStripeCheckout,
  closeStripeCheckout,
  setStripeVisible,
  verifyStripeReceipt,
} = useStripeCheckout()

// 容错解析：兼容字符串 / 已是对象 / 解析失败
function safeParse(raw: any) {
  if (raw == null)
    return null
  if (typeof raw === 'object')
    return raw
  try {
    return JSON.parse(raw)
  }
  catch {
    return null
  }
}

// H5 → 游戏：发送 command（本地联调用 '*'，生产需替换为真实 Unity origin）
function sendCommandToGame(commandId: string, data: Record<string, any> = {}) {
  const win = iframeRef.value?.contentWindow
  if (!win) {
    console.warn('[PAY-BRIDGE] iframe contentWindow 不存在，无法回传')
    return
  }
  const payload = {
    type: 'command',
    data: JSON.stringify({ command_id: commandId, data: JSON.stringify(data) }),
  }
  // iframe 内容与本页同源（/content/read/），按同源 origin 投递，避免使用通配 '*'
  win.postMessage(payload, window.location.origin)
}

// 游戏 → H5：收到支付请求
function handleU2HPayOrder(flutterPayDataRaw: any) {
  const payData = safeParse(flutterPayDataRaw)

  // 参数校验：解析失败或缺关键字段时不向后端发起下单，直接回传失败
  const product = payData?.product
  if (!payData || typeof payData !== 'object' || !product?.extendProductId) {
    console.error('[PAY-BRIDGE] 支付请求参数非法，已忽略:', payData)
    sendCommandToGame('h2u_pay_failed', {})
    return
  }

  startStripePay(payData)
}

// 游戏内发起 Stripe 内嵌支付
async function startStripePay(payData: any) {
  // product 为服务端返回的商品结构（ProductGroupItem），字段透传给内嵌接口
  const product = payData?.product || {}
  // successUrl/cancelUrl 后端为必填，需照常传；是否跳转由后端创建 Session 时
  // redirect_on_completion: 'never' 控制，前端不靠省略 url 规避
  const params = {
    ...product,
    contentId: payData?.contentId,
    contentChapterId: payData?.chapterId,
    memberCurrencyCode: 'usd',
    payType: 5, // stripe
    type: 1, // 阅读页面（游戏内容内）
    successUrl: window.location.href,
    cancelUrl: window.location.href,
  }
  try {
    // extendProductId 来自游戏透传的商品信息，验单时需回传
    await openStripeCheckout(params, product.extendProductId)
  }
  catch (e) {
    console.error('[PAY-BRIDGE] 创建 Stripe 内嵌支付失败:', e)
    sendCommandToGame('h2u_pay_failed', {})
  }
}

// 内嵌支付完成：验单后回传游戏结果
async function onGameStripeComplete() {
  const ok = await verifyStripeReceipt()
  closeStripeCheckout()
  sendCommandToGame(ok ? 'h2u_pay_success' : 'h2u_pay_failed', {})
}

// 用户主动关闭内嵌弹窗：回传取消
function onGameStripeCancel() {
  closeStripeCheckout()
  sendCommandToGame('h2u_pay_cancled', {})
}

// 进度类消息已由 useGameFrame 处理，这里只处理业务消息（来源已校验）
function onGameMessage(event: MessageEvent) {
  const { type } = event.data ?? {}

  switch (type) {
    case 'return_to_home': {
      userStore.init({ force: true })
      // 返回书籍详情页；query 上无 contentId 时兜底回书城
      const contentId = (route.query.contentId as string) || ''
      if (contentId)
        router.replace({ name: 'book', params: { contentId } })
      else
        router.replace({ name: 'home' })
      break
    }
    case 'navigate_external':
      if (event.data.url)
        window.location.replace(event.data.url)
      break
    case 'command': {
      // 改进版协议：{ type:'command', data: CommandData 序列化字符串 }
      const cmd = safeParse(event.data.data)
      if (cmd?.command_id === 'u2h_pay_order')
        handleU2HPayOrder(cmd.data)
      break
    }
    case 'u2h_pay_order':
      // 旧版协议：直接 type 为 u2h_pay_order，data 为 FlutterPayData 字符串
      handleU2HPayOrder(event.data.data)
      break
  }
}

function onIframeLoad() {
  try {
    const iframePath = iframeRef.value?.contentWindow?.location?.pathname ?? ''
    if (iframePath.startsWith('/content/') && !iframePath.startsWith('/content/read/')) {
      const contentId = iframePath.replace('/content/', '')
      stopTips()
      showLoading.value = false
      router.replace({ name: 'book', params: { contentId } })
    }
  }
  catch {}
}

function onPopState() {
  window.removeEventListener('popstate', onPopState)
  userStore.init({ force: true })
  router.back()
}

onMounted(() => {
  reportPageView()

  // iframe 全屏覆盖会劫持浏览器返回事件，推入一个守卫历史记录
  // 确保用户按返回时 popstate 能传递到父窗口
  history.pushState({ gameGuard: true }, '', location.href)
  window.addEventListener('popstate', onPopState)
})

onUnmounted(() => {
  window.removeEventListener('popstate', onPopState)
})
</script>

<template>
  <div class="game-page">
    <BizGameLoading
      :visible="showLoading"
      :cover-url="coverUrl"
      :animation-url="loadingAnimationUrl"
      :percent="percent"
      :tip="currentTip"
    />
    <!-- 等 playTraceId 落到路由后再挂载，避免 router.replace 触发引擎二次加载 -->
    <iframe v-if="route.query.playTraceId" ref="iframeRef" :src="gameUrl" class="game-frame" allowfullscreen @load="onIframeLoad" />

    <!-- Stripe 内嵌支付弹窗（游戏内） -->
    <StripeCheckoutPopup
      :value="stripeVisible"
      :client-secret="stripeClientSecret"
      @input="setStripeVisible"
      @cancel="onGameStripeCancel"
      @complete="onGameStripeComplete"
    />
  </div>
</template>

<style lang="scss" scoped>
.game-page {
  position: fixed;
  inset: 0;
  background: #231F20;
}

.game-frame {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: none;
}
</style>
